import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { oohData } from '../utils/oohData';
import { FlameIcon, AlertTriangleIcon, MousePointerClickIcon } from 'lucide-react';
type View = 'clean' | 'aoi';
const VERDICT_STYLES = {
  high: 'bg-green-100 text-green-800 border-green-200',
  low: 'bg-amber-100 text-amber-800 border-amber-200'
};
export const AttentionHeatmap: React.FC = () => {
  const {
    attentionHeatmap
  } = oohData;
  const {
    images,
    predictedScore,
    aois,
    nuance,
    objectiveRead
  } = attentionHeatmap;
  const [view, setView] = useState<View>('clean');
  // The AOI view is easy to miss, so it is hinted with a pulsing dot and a
  // one-line prompt until the reader has opened it once.
  const [hasSeenAoi, setHasSeenAoi] = useState(false);
  const selectView = (next: View) => {
    setView(next);
    if (next === 'aoi') setHasSeenAoi(true);
  };

  // During PDF capture the toggle can't be used, so render BOTH views
  // stacked and labelled — otherwise only the active tab would be captured.
  const [isExporting, setIsExporting] = useState(false);
  useEffect(() => {
    const start = () => setIsExporting(true);
    const end = () => setIsExporting(false);
    window.addEventListener('report:export-start', start);
    window.addEventListener('report:export-end', end);
    return () => {
      window.removeEventListener('report:export-start', start);
      window.removeEventListener('report:export-end', end);
    };
  }, []);
  return <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-18" data-mp="1d8bajkqpysxa">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5" data-id="element-19" data-mp="1i7xo5vagmb6m">
        <div data-id="element-20" data-mp="1n70stemj2fe2">
          <div className="flex items-center space-x-3 mb-2" data-id="element-21" data-mp="1n7jxvur2x9ao">
            <div className="p-2 bg-[#1E90FF]/10 text-[#1E90FF] rounded-lg" data-id="element-22" data-mp="1n4xt22vkp7w5">
              <FlameIcon className="w-5 h-5" data-id="element-23" data-mp="2tmkvs5zo01jw" />
            </div>
            <h2 className="text-lg font-medium text-gray-800" data-id="element-24" data-mp="2ylo0fpbqg5rc">
              {attentionHeatmap.title}
            </h2>
          </div>
          <p className="text-sm text-gray-600 max-w-2xl" data-id="element-25" data-mp="yid3ayuibqab">
            {attentionHeatmap.subtitle}
          </p>
        </div>

        {/* View toggle — interactive only, excluded from the PDF */}
        <div data-export-hide className="inline-flex bg-gray-100 rounded-lg p-1 flex-shrink-0" role="tablist" aria-label="Heatmap view" data-id="element-26" data-mp="17jb7pi8uxztf">
          {([['clean', 'Heatmap'], ['aoi', 'Heatmap + AOIs']] as const).map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => selectView(key)} className={`relative px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === key ? 'text-[#1E90FF]' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-27" data-mp="oli7inpmc3zq">
              {/* Active pill slides between tabs */}
              {view === key && <motion.span layoutId="heatmapTabPill" className="absolute inset-0 bg-white rounded-md shadow-sm" transition={{
            type: 'spring',
            stiffness: 420,
            damping: 34
          }} data-id="element-28" data-mp="3ix38klz212jr" />}
              <span className="relative inline-flex items-center gap-1.5" data-id="element-29" data-mp="qf48ybtwjf5">
                {label}
                {key === 'aoi' && !hasSeenAoi && <motion.span className="w-1.5 h-1.5 rounded-full bg-[#1E90FF]" animate={{
              scale: [1, 1.6, 1],
              opacity: [1, 0.35, 1]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-30" data-mp="5o6tfkl39rfn" />}
              </span>
            </button>)}
        </div>
      </div>

      {/* Heatmap image(s) */}
      {isExporting ? <div className="space-y-4" data-id="element-31" data-mp="2rwxvenc6509n">
          {([['clean', 'Attention heatmap'], ['aoi', 'Attention heatmap with areas of interest']] as const).map(([key, caption]) => <figure key={key} data-id="element-32" data-mp="2hxoxjje6n1i0">
              <img src={images[key]} alt={caption} className="w-full h-auto rounded-xl border border-gray-100" data-id="element-33" data-mp="3fo3c14pxnwl0" />
              <figcaption className="text-xs text-gray-500 mt-2" data-id="element-34" data-mp="3ammenj6nqs96">
                {caption}
              </figcaption>
            </figure>)}
        </div> : <div data-id="element-35" data-mp="30q18guiwjb3r">
          <AnimatePresence mode="wait" initial={false} data-id="element-36" data-mp="30sndonibleit">
            <motion.img key={view} src={view === 'clean' ? images.clean : images.aoi} alt={view === 'clean' ? 'Attention heatmap of the creative' : 'Attention heatmap with areas of interest and attention percentages'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="w-full h-auto rounded-xl border border-gray-100" data-id="element-37" data-mp="1uba3aemqx43x" />
          </AnimatePresence>

          {/* Prompt that the AOI view exists, until it has been opened */}
          <AnimatePresence data-id="element-38" data-mp="shmnn03s3vdp">
            {!hasSeenAoi && <motion.button type="button" data-export-hide onClick={() => selectView('aoi')} initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.25
        }} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1E90FF] hover:underline" data-id="element-39" data-mp="xhr0mg5ha7z6">
                <motion.span animate={{
            y: [0, -2, 0]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="inline-flex" data-id="element-40" data-mp="2w7sp7n61kh1p">
                  <MousePointerClickIcon className="w-3.5 h-3.5" data-id="element-41" data-mp="315aoelu9bhwi" />
                </motion.span>
                Switch to “Heatmap + AOIs” to see attention per element
              </motion.button>}
          </AnimatePresence>
        </div>}

      {/* Model credibility — leads with the strength of the model */}
      <div className="mt-4 flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-42" data-mp="3jx3fgaprmt1l">
        <p className="flex-1 text-xs text-gray-600 leading-relaxed" data-id="element-43" data-mp="3jz6f7mgq00ji">
          {attentionHeatmap.credibility}
        </p>
        <div className="flex flex-wrap gap-2 lg:justify-end lg:flex-shrink-0" data-id="element-44" data-mp="3tv9k302yida5">
          {attentionHeatmap.modelStats.map(stat => <span key={stat} className="export-text-up bg-gray-50 border border-gray-100 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 whitespace-nowrap" data-id="element-45" data-mp="hcqc5lgwo68t">
              {stat}
            </span>)}
        </div>
      </div>

      {/* Predicted attention score */}
      <div className="mt-6 p-5 rounded-xl border border-gray-100 bg-gray-50/50" data-id="element-46" data-mp="car5a1lyir74">
        <div className="flex items-center justify-between mb-2" data-id="element-47" data-mp="3tsad7ma671ly">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-48" data-mp="2exw8s7g5fu3">
            Brandpulse Attention Score
          </h3>
          <div className="flex items-baseline space-x-2" data-id="element-49" data-mp="3kt6w2nukcwa3">
            <span className="text-2xl font-semibold text-gray-900" data-id="element-50" data-mp="3kqkpaqfhyksx">
              {predictedScore.value}
              <span className="text-base font-medium text-gray-400" data-id="element-51" data-mp="3au7svkskhsuh">
                {' '}
                / {predictedScore.max}
              </span>
            </span>
            <span className="export-text-up inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-amber-100 text-amber-800 border-amber-200" data-id="element-52" data-mp="3fsj2pviv43cp">
              {predictedScore.label}
            </span>
          </div>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" role="img" aria-label={`Brandpulse attention score ${predictedScore.value} out of ${predictedScore.max}`} data-id="element-53" data-mp="3m9und2ifr32s">
          <div className="h-full bg-amber-500 rounded-full" style={{
          width: `${predictedScore.value}%`
        }} data-id="element-54" data-mp="3cd9gebmuvhwb" />
        </div>
        <p className="text-xs text-gray-500 leading-relaxed mt-3" data-id="element-55" data-mp="33jwguquadi19">
          {predictedScore.caption}
        </p>
      </div>

      {/* AOI measurements vs benchmark */}
      <div className="mt-6" data-id="element-56" data-mp="2yiyo5kbnh5li">
        <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-57" data-mp="2tlp30gvang3h">
          Attention by element
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-58" data-mp="2omdj4yzmruho">
          {aois.map(aoi => <div key={aoi.label} className="p-5 rounded-xl border border-gray-100 bg-gray-50/50 flex flex-col" data-id="element-59" data-mp="2ejgjjw7t4ofn">
              <div className="flex items-center justify-between mb-3" data-id="element-60" data-mp="2jg71yq733w48">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-61" data-mp="2oifuxmjyiy6c">
                  {aoi.label}
                </h4>
                <span className={`export-text-up inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${VERDICT_STYLES[aoi.verdict]}`} data-id="element-62" data-mp="2tfg0zxgt40xg">
                  {aoi.verdict}
                </span>
              </div>
              <p className="text-3xl font-semibold text-gray-900 leading-none mb-2" data-id="element-63" data-mp="25pv6chbajf8c">
                {aoi.value}%
              </p>
              <p className="text-xs text-gray-500 mb-3" data-id="element-64" data-mp="2ajg3k752856h">
                vs {aoi.benchmark}% category benchmark
              </p>
              {/* Value bar with benchmark marker */}
              <div className="relative w-full h-1.5 bg-gray-200 rounded-full mb-3" data-id="element-65" data-mp="1hz7whqhtefj5">
                <div className={`h-full rounded-full ${aoi.verdict === 'high' ? 'bg-green-500' : 'bg-amber-500'}`} style={{
              width: `${Math.min(aoi.value, 100)}%`
            }} data-id="element-66" data-mp="vqblb0lhwuni" />
                <span className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-gray-400 rounded-full" style={{
              left: `${Math.min(aoi.benchmark, 100)}%`
            }} aria-hidden="true" data-id="element-67" data-mp="15q2gz5lslfp6" />
              </div>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-68" data-mp="i0rkw1fe09hd">
                {aoi.note}
              </p>
            </div>)}
        </div>

        {/* What the distribution means against the stated objective */}
        <p className="mt-4 text-sm text-gray-700 leading-relaxed bg-gray-50/50 border border-gray-100 rounded-xl p-4" data-id="element-69" data-mp="qtbcwj16bft8">
          <span className="font-semibold text-gray-900" data-id="element-70" data-mp="3qdp1imeb8m7c">
            Against the objective:{' '}
          </span>
          {objectiveRead}
        </p>
      </div>

      {/* The key nuance: visibility ≠ legibility */}
      <div className="mt-6 bg-amber-50/60 border border-amber-200 rounded-xl p-5 flex items-start space-x-3" data-id="element-71" data-mp="3hyr5u755dbrg">
        <AlertTriangleIcon className="export-icon-down w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-72" data-mp="3dwe9g9caiq7c" />
        <div data-id="element-73" data-mp="38ym8yqxkxvrj">
          <h3 className="text-sm font-semibold text-amber-900 mb-1" data-id="element-74" data-mp="38wroteu4u7oi">
            {nuance.heading}
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-75" data-mp="340az4g7abl9b">{nuance.body}</p>
        </div>
      </div>
    </div>;
};